import { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getDirectory } from "../../services/Registre_service";
import {
  Spin,
  Empty,

  Button,
  message,
  Tabs,
  Typography,
  Space,
} from "antd";
import type { TabsProps } from "antd";
import { CrownOutlined } from "@ant-design/icons";

// Composants
import SearchFilters from "../../components/SearchFilters";
import ExportButtons from "../../components/ExportButtons";
import DirectoryTable from "../../components/DirectoryTable";
import PremiumModal from "../../components/PremiumModal";
import CityExportModal from "../../components/CityExportModal";
import ProfessionExportModal from "../../components/ProfessionExportModal";
import HeaderActions from "../../components/HeaderActions";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Services & Utils
import {
  checkPremiumAvailability,
  getAllProfessions,
  getAllExportPrices,
  PremiumAvailability,
  PremiumPurchaseResult,
  ProfessionSetting,
  ExportProfessionInfo,
  getAvailableCities,
  calculateCityExportPrice,
} from "../../services/professionService";

// Types & Constants
import { DirectoryItem, COLORS } from "../../types/types";

const { Title } = Typography;

const Annuaire = () => {
  const navigate = useNavigate();

  // États principaux
  const [users, setUsers] = useState<DirectoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string>("");

  // Recherche et filtres
  const [searchText, setSearchText] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<string | undefined>(undefined);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalItems, setTotalItems] = useState(0);

  // Données de référence
  const [allProfessions, setAllProfessions] = useState<ProfessionSetting[]>([]);
  const [exportProfessions, setExportProfessions] = useState<ExportProfessionInfo[]>([]);
  const [availableCities, setAvailableCities] = useState<any[]>([]);
  const [cityStats, setCityStats] = useState<Record<string, number>>({});

  // États utilisateur courant
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [currentUser, setCurrentUser] = useState<DirectoryItem | null>(null);

  // États modales
  const [premiumModal, setPremiumModal] = useState({
    visible: false,
    profession: null as string | null,
    availability: null as PremiumAvailability | null,
    checking: false,
    step: 0,
    result: null as PremiumPurchaseResult | null
  });

  const [exportModal, setExportModal] = useState({
    profession: null as ExportProfessionInfo | null,
    amount: 0,
    visible: false,
    showPayment: false
  });

  const [cityModal, setCityModal] = useState({
    city: null as any | null,
    calculation: null as any | null,
    amount: 0,
    visible: false,
    showPayment: false
  });

  // Chargement des données initiales
  useEffect(() => {
    loadCurrentUser();
    loadAllProfessions();
    loadExportProfessions();
    loadAvailableCities();
  }, []);

  const loadCurrentUser = () => {
    const userId = localStorage.getItem("userId");
    if (!userId) return;

    setCurrentUserId(Number(userId));
    setCurrentUser({
      id: Number(userId),
      show_in_directory: localStorage.getItem("show_in_directory") === "true",
      is_premium_directory: localStorage.getItem("is_premium_directory") === "true",
      profession: localStorage.getItem("userProfession") || undefined,
    } as DirectoryItem);
  };

  const loadAllProfessions = async () => {
    try {
      const professions = await getAllProfessions();
      setAllProfessions(professions);
    } catch (error) {
      console.error("Erreur chargement professions:", error);
    }
  };

  const loadExportProfessions = async () => {
    try {
      const professions = await getAllExportPrices();
      setExportProfessions(professions);
    } catch (error) {
      console.error("Erreur export prices:", error);
    }
  };

  const loadAvailableCities = async () => {
    try {
      const cities = await getAvailableCities();
      setAvailableCities(cities);
    } catch (error) {
      console.error("Erreur chargement villes:", error);
    }
  };

 const fetchUsers = useCallback(async () => {
  try {
    setLoading(true);
    setError(null);
    
    const response = await getDirectory(
      currentPage, 
      pageSize, 
      activeTab !== "Autres" ? activeTab : undefined,
      selectedCountry 
    );
    
    setUsers(Array.isArray(response.data) ? response.data : []);
    setTotalItems(response.pagination?.total || 0);
    setCityStats(response.metadata?.cities || {});
  } catch (error: any) {
    setError(error.message || "Erreur réseau");
    setUsers([]);
  } finally {
    setLoading(false);
  }
}, [currentPage, pageSize, activeTab, selectedCountry]); 

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (!currentUserId || users.length === 0) return;
    const updatedUser = users.find(u => u.id === currentUserId);
    if (updatedUser) setCurrentUser(updatedUser);
  }, [users, currentUserId]);

  // Professions prioritaires
  const priorityProfessionsList = useMemo((): string[] => {
    const priorityFromSettings = allProfessions
      .filter(p => p.is_priority === true)
      .map(p => p.name)
      .filter((name): name is string => name !== undefined);

    if (priorityFromSettings.length > 0) return priorityFromSettings;

    const priorityFromUsers = users
      .filter(u => u.professionSetting?.is_priority === true && u.profession)
      .map(u => u.profession)
      .filter((prof): prof is string => prof !== undefined);

    return [...new Set(priorityFromUsers)].sort();
  }, [allProfessions, users]);

  useEffect(() => {
    if (!activeTab && priorityProfessionsList.length > 0) {
      setActiveTab(priorityProfessionsList[0] || "Autres");
    } else if (!activeTab) {
      setActiveTab("Autres");
    }
  }, [priorityProfessionsList, activeTab]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  // Filtrage
  const getFilteredUsers = useCallback(() => {
    let filtered = [...users];

    if (selectedCountry) {
      filtered = filtered.filter((user) =>
        user.country === selectedCountry || user.ville === selectedCountry
      );
    }

    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter((user) => {
        const fullName = user.type === "client"
          ? `${user.prenom || ''} ${user.nom || ''}`.toLowerCase()
          : `${user.firstname || ''} ${user.name || ''}`.toLowerCase();

        return (
          fullName.includes(searchLower) ||
          (user.profession && user.profession.toLowerCase().includes(searchLower)) ||
          (user.email && user.email.toLowerCase().includes(searchLower)) ||
          (user.company && user.company.toLowerCase().includes(searchLower)) ||
          (user.adresse && user.adresse.toLowerCase().includes(searchLower)) ||
          (user.country && user.country.toLowerCase().includes(searchLower)) ||
          (user.ville && user.ville.toLowerCase().includes(searchLower))
        );
      });
    }

    return filtered;
  }, [users, selectedCountry, searchText]);

  const displayedUsers = getFilteredUsers();

  // Handlers
  const handleGoToDirectoryVisibility = () => {
    navigate("/home/gestion-compte", {
      state: { activeTab: "professional", scrollTo: "directory" },
    });
  };

  const handleOpenPremiumPurchase = async (profession: string) => {
    try {
      setPremiumModal({
        ...premiumModal,
        visible: true,
        profession,
        checking: true,
        step: 0
      });

      const professionSetting = allProfessions.find(p => p.name === profession);
      if (!professionSetting?.id) {
        message.error("Cette profession n'est pas éligible");
        setPremiumModal(prev => ({ ...prev, visible: false }));
        return;
      }

      const availability = await checkPremiumAvailability(professionSetting.id);
      setPremiumModal(prev => ({
        ...prev,
        availability,
        checking: false
      }));
    } catch (error: any) {
      message.error(error.message);
      setPremiumModal(prev => ({ ...prev, visible: false }));
    }
  };

  const handlePageChange = (page: number, pageSize?: number) => {
    setCurrentPage(page);
    if (pageSize) setPageSize(pageSize);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePageSizeChange = (current: number, size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };



  // Fonction pour gérer la sélection d'une ville (appelée depuis ExportButtons)
const handleCitySelect = async (city: any) => {
  try {
    const contactCount = cityStats[city.name] || 0;
    if (contactCount === 0) {
      message.warning(`Aucun contact à ${city.name}`);
      return;
    }

    const calculation = await calculateCityExportPrice(city.name, contactCount);

    console.log("[DEBUG] handleCitySelect - Calculation:", calculation);
    
    if (!calculation.available) {
      message.warning(calculation.message);
      return;
    }
    
    if (calculation.totalPrice > 0) {
      // Ville payante - Afficher la modale
      setCityModal({
        city,
        calculation,
        amount: calculation.totalPrice,
        visible: true,
        showPayment: false
      });
    } else {
      // Ville gratuite - Export direct
      await exportCityUsers(city, contactCount);
    }
  } catch (error: any) {
    message.error(error.message);
  }
};

// Fonction pour exporter après paiement (appelée depuis CityExportModal)
const handleCityExport = async (city: any, contactCount: number) => {
  try {
    await exportCityUsers(city, contactCount);
  } catch (error: any) {
    message.error(error.message);
  }
};
  const hideDirectoryButton = !!currentUser &&
    !currentUser.is_premium_directory &&
    currentUser.show_in_directory === true;

  // Configuration des tabs
  const tabItems: TabsProps['items'] = useMemo(() => {
    const items: TabsProps['items'] = [
      { key: 'Autres', label: <span>Autres professions</span> },
    ];

    priorityProfessionsList.slice(0, 5).forEach(prof => {
      items.push({
        key: prof,
        label: (
          <Space>
            <CrownOutlined style={{ color: COLORS.secondary }} />
            {prof.length > 20 ? `${prof.substring(0, 20)}...` : prof}
          </Space>
        ),
      });
    });

    return items;
  }, [priorityProfessionsList]);

// Fonction pour exporter tous les contacts d'une ville
const exportCityUsers = async (city: any, contactCount: number) => {
  try {
    setLoading(true);
    const loadingKey = 'export-city';
    
    message.loading({ 
      content: `Récupération des contacts à ${city.name}...`, 
      key: loadingKey, 
      duration: 0 
    });

    const pageSizeForExport = 500;
    let page = 1;
    let allUsers: DirectoryItem[] = [];
    let totalPages = 1;

    while (page <= totalPages) {
      const response = await getDirectory(page, pageSizeForExport, undefined, city.name);
      
      if (response.data && Array.isArray(response.data)) {
        allUsers = [...allUsers, ...response.data];
      }

      if (response.pagination) {
        totalPages = response.pagination.totalPages || 1;
      }

      message.loading({ 
        content: `Récupération: ${allUsers.length}/${contactCount} contacts...`, 
        key: loadingKey, 
        duration: 0 
      });

      page++;
    }

    if (allUsers.length > 0) {
      // Générer l'Excel
      const dataToExport = allUsers.map((user: DirectoryItem) => {
        let prenom = "";
        let nom = "";
        
        if (user.type === "client") {
          prenom = user.prenom || "";
          nom = user.nom || "";
        } else {
          prenom = user.firstname || "";
          nom = user.name || "";
        }

        return {
          "EMAIL": user.email || "",
          "PROFESSION": user.profession || "",
          "NOM": nom,                         
          "PRÉNOM": prenom,                    
          "ADRESSE": user.adresse || "",
          "CODE POSTAL": user.code_postal || user.postal_code || "",
          "VILLE": user.ville || user.country || "",
          "TÉLÉPHONE": user.telephone || user.number || "",
          "FORME JURIDIQUE": user.formatJuridique || "",
          "DIRIGEANT": user.dirigeant || "",
          "IMMATRICULATION": user.siret || user.siren || "",
          "ANNÉE CA": user.anneeCloture || "",
          "CA": user.chiffreAffaire || "",
          "RÉSULTAT": user.resultat || "",
          "EFFECTIF": user.effectifDescription || "",
          "NAF APE": user.naf_ape || user.code_NAF || "",
          "SIRET": user.siret || user.siren || "",
          "EFFECTIF MIN": user.effectifMin || "",
          "EFFECTIF MAX": user.effectifMax || "",
          "TVA INTRA": user.tva_intra || "",
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      
      // Ajuster la largeur des colonnes
      worksheet["!cols"] = [
        { wch: 25 }, { wch: 30 }, { wch: 15 }, { wch: 20 },
        { wch: 20 }, { wch: 30 }, { wch: 30 }, { wch: 25 },
        { wch: 25 }, { wch: 20 }, { wch: 20 }, { wch: 15 },
        { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 20 },
        { wch: 15 }
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Annuaire");
      
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const file = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const fileName = `annuaire_${city.name}_${allUsers.length}_contacts_${new Date().toISOString().slice(0, 10)}.xlsx`;
      saveAs(file, fileName);
      
      message.success({ 
        content: `✅ Export réussi : ${allUsers.length} contacts de ${city.name} téléchargés !`, 
        key: loadingKey,
        duration: 5 
      });
    } else {
      message.warning({ 
        content: `Aucun contact trouvé à ${city.name}`, 
        key: loadingKey, 
        duration: 3 
      });
    }
  } catch (error: any) {
    console.error("❌ ERREUR EXPORT VILLE:", error);
    message.error({ 
      content: error.message || "Erreur lors de l'export", 
      key: 'export-city', 
      duration: 3 
    });
  } finally {
    setLoading(false);
  }
};

// Fonction pour exporter tous les contacts d'une profession
const exportProfessionUsers = async (profession: ExportProfessionInfo) => {
  try {
    setLoading(true);
    const loadingKey = 'export-profession';
    
    message.loading({ 
      content: `Récupération des ${profession.name}...`, 
      key: loadingKey, 
      duration: 0 
    });

    const pageSizeForExport = 500;
    let page = 1;
    let allUsers: DirectoryItem[] = [];
    let totalPages = 1;

    while (page <= totalPages) {
      const response = await getDirectory(page, pageSizeForExport, profession.name);
      
      if (response.data && Array.isArray(response.data)) {
        allUsers = [...allUsers, ...response.data];
      }

      if (response.pagination) {
        totalPages = response.pagination.totalPages || 1;
      }

      message.loading({ 
        content: `Récupération: ${allUsers.length} ${profession.name}...`, 
        key: loadingKey, 
        duration: 0 
      });

      page++;
    }

    if (allUsers.length > 0) {
      // Générer l'Excel
      const dataToExport = allUsers.map((user: DirectoryItem) => {
        let prenom = "";
        let nom = "";
        
        if (user.type === "client") {
          prenom = user.prenom || "";
          nom = user.nom || "";
        } else {
          prenom = user.firstname || "";
          nom = user.name || "";
        }

        return {
          "EMAIL": user.email || "",
          "PROFESSION": user.profession || "",
          "NOM": nom,                         
          "PRÉNOM": prenom,                    
          "ADRESSE": user.adresse || "",
          "CODE POSTAL": user.code_postal || user.postal_code || "",
          "VILLE": user.ville || user.country || "",
          "TÉLÉPHONE": user.telephone || user.number || "",
          "FORME JURIDIQUE": user.formatJuridique || "",
          "DIRIGEANT": user.dirigeant || "",
          "IMMATRICULATION": user.siret || user.siren || "",
          "ANNÉE CA": user.anneeCloture || "",
          "CA": user.chiffreAffaire || "",
          "RÉSULTAT": user.resultat || "",
          "EFFECTIF": user.effectifDescription || "",
          "NAF APE": user.naf_ape || user.code_NAF || "",
          "SIRET": user.siret || user.siren || "",
          "EFFECTIF MIN": user.effectifMin || "",
          "EFFECTIF MAX": user.effectifMax || "",
          "TVA INTRA": user.tva_intra || "",
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(dataToExport);
      
      // Ajuster la largeur des colonnes
      worksheet["!cols"] = [
        { wch: 25 }, { wch: 30 }, { wch: 15 }, { wch: 20 },
        { wch: 20 }, { wch: 30 }, { wch: 30 }, { wch: 25 },
        { wch: 25 }, { wch: 20 }, { wch: 20 }, { wch: 15 },
        { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 20 },
        { wch: 15 }
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Annuaire");
      
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const file = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const professionLabel = profession.name.replace(/\s+/g, "_").replace(/[^\w\-]/g, "");
      const fileName = `annuaire_${professionLabel}_${allUsers.length}_contacts_${new Date().toISOString().slice(0, 10)}.xlsx`;
      saveAs(file, fileName);
      
      message.success({ 
        content: `✅ Export réussi : ${allUsers.length} ${profession.name} téléchargés !`, 
        key: loadingKey,
        duration: 5 
      });
    } else {
      message.warning({ 
        content: `Aucun ${profession.name} trouvé dans l'annuaire`, 
        key: loadingKey, 
        duration: 3 
      });
    }
  } catch (error: any) {
    console.error("❌ ERREUR EXPORT PROFESSION:", error);
    message.error({ 
      content: error.message || "Erreur lors de l'export", 
      key: 'export-profession', 
      duration: 3 
    });
  } finally {
    setLoading(false);
  }
};

  return (
    <div style={{ maxWidth: "100%", margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0, color: COLORS.text, fontWeight: 500 }}>
          Annuaire des professionnels
        </Title>

        <HeaderActions
          currentUser={currentUser}
          hideDirectoryButton={hideDirectoryButton}
          onDirectoryVisibility={handleGoToDirectoryVisibility}
          onOpenPremium={handleOpenPremiumPurchase}
        />
      </div>

      {/* Recherche et filtres */}
      <SearchFilters
        searchText={searchText}
        onSearchChange={setSearchText}
        selectedCountry={selectedCountry}
        onCountryChange={setSelectedCountry}
        cityStats={cityStats}
      />

      {/* Boutons d'export */}
      <ExportButtons
        users={users}
        exportProfessions={exportProfessions}
        availableCities={availableCities}
        cityStats={cityStats}
        currentUserId={currentUserId}
        onExportProfession={(profession, amount) => {
          setExportModal({
            profession,
            amount: Number(amount),
            visible: true,
            showPayment: false
          });
        }}
        onExportCity={handleCitySelect} 
      />

      {/* Navigation Tabs */}
      <div style={{ marginBottom: 24 }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          type="card"
          size="small"
        />
      </div>

      {/* Tableau */}
      {error ? (
        <Empty description={error}>
          <Button onClick={fetchUsers}>Réessayer</Button>
        </Empty>
      ) : loading ? (
        <div style={{ textAlign: "center", padding: 60 }}><Spin size="large" /></div>
      ) : displayedUsers.length === 0 ? (
        <Empty description="Aucun professionnel trouvé" />
      ) : (
        <>
          <DirectoryTable
            users={displayedUsers}
            totalItems={totalItems}
            currentPage={currentPage}
            pageSize={pageSize}
            activeTab={activeTab}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}

      {/* Modales */}
      <ProfessionExportModal
        visible={exportModal.visible}
        profession={exportModal.profession}
        amount={exportModal.amount}
        showPayment={exportModal.showPayment}
        userId={currentUserId}
        onCancel={() => setExportModal({ ...exportModal, visible: false })}
        onShowPayment={() => setExportModal(prev => ({ ...prev, showPayment: true }))}
        onSuccess={() => {
          setExportModal({ ...exportModal, visible: false });
        }}
        onExport={exportProfessionUsers}  

      />

      <CityExportModal
        visible={cityModal.visible}
        city={cityModal.city}
        calculation={cityModal.calculation}
        amount={cityModal.amount}
        showPayment={cityModal.showPayment}
        userId={currentUserId}
        onCancel={() => setCityModal({ ...cityModal, visible: false })}
        onShowPayment={() => setCityModal(prev => ({ ...prev, showPayment: true }))}
        onSuccess={() => {
          setCityModal({ ...cityModal, visible: false });
          // Rafraîchir les données si nécessaire
        }}
        onExportCity={handleCityExport}
      />

      <PremiumModal
        visible={premiumModal.visible}
        profession={premiumModal.profession}
        availability={premiumModal.availability}
        checking={premiumModal.checking}
        step={premiumModal.step}
        result={premiumModal.result}
        userId={currentUserId}
        onCancel={() => setPremiumModal(prev => ({ ...prev, visible: false }))}
        onPayment={() => setPremiumModal(prev => ({ ...prev, step: 1 }))}
        onSuccess={(position) => {
          setPremiumModal(prev => ({ ...prev, step: 2 }));
          setTimeout(() => {
            setPremiumModal(prev => ({ ...prev, visible: false }));
            fetchUsers();
          }, 1500);
        }}
      />
    </div>
  );
};

export default Annuaire;